import React, { useState } from 'react';
import { createPortal } from 'react-dom';

const SyncDocsModal = ({ isOpen, onClose, changes, onApply, isLoading }) => {
  const [approvedAgents, setApprovedAgents] = useState(new Set());

  React.useEffect(() => {
    if (changes && changes.length > 0) {
      setApprovedAgents(new Set(changes.map(c => c.agent_id)));
    }
  }, [changes]);

  const toggleAgent = (agentId) => {
    setApprovedAgents(prev => {
      const next = new Set(prev);
      if (next.has(agentId)) next.delete(agentId);
      else next.add(agentId);
      return next;
    });
  };

  const selectAll = () => setApprovedAgents(new Set(changes.map(c => c.agent_id)));
  const deselectAll = () => setApprovedAgents(new Set());

  const handleApply = () => {
    onApply(changes.filter(c => approvedAgents.has(c.agent_id)));
  };

  if (!isOpen) return null;

  const hasChanges = changes && changes.length > 0;
  const totalAdded = hasChanges ? changes.reduce((sum, c) => sum + c.added_docs.length, 0) : 0;
  const totalRemoved = hasChanges ? changes.reduce((sum, c) => sum + c.removed_docs.length, 0) : 0;

  const modal = (
    <div
      className="modal fade show d-block bg-dark bg-opacity-50"
      style={{ zIndex: 'var(--num-10000000000)' }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-lg modal-dialog-centered sync-docs-modal-dialog"
        onClick={e => e.stopPropagation()}
      >
        <div className="modal-content border-0 shadow sync-docs-modal-content">

          {/* Header */}
          <div className="d-flex justify-content-between align-items-center px-3 py-2 sync-docs-modal-header">
            <div className="d-flex align-items-center gap-2">
              <i className="bi bi-database-check ev-reg-p-blue ev-fs-16"></i>
              <div>
                <span className="ev-bold-reg ev-reg-p-dark ev-fs-14">Sync Documents with tags</span>
                <p className="ev-xxsmall-p-blue mb-0 sync-docs-subtitle">
                  Match documents to agents based on their tags. Use after uploading new docs, changing threads, or editing document tags.
                </p>
              </div>
            </div>
            <button type="button" className="ev-btn-text-only-fill ev-fs-20 align-self-start" onClick={onClose}>
              <i className="bi bi-x"></i>
            </button>
          </div>

          {/* Body */}
          <div className="scrollable-element px-3 pt-2 sync-docs-modal-body">

            {isLoading ? (
              <div className="d-flex flex-column align-items-center justify-content-center sync-docs-center-state">
                <div className="spinner-border text-primary sync-docs-spinner" role="status"></div>
                <p className="ev-reg-p-dark mt-3 mb-0">Scanning for document changes...</p>
              </div>

            ) : !hasChanges ? (
              <div className="d-flex flex-column align-items-center justify-content-center sync-docs-center-state">
                <i className="bi bi-check-circle-fill text-success sync-docs-check-icon"></i>
                <p className="ev-bold-reg ev-reg-p-dark mt-3 mb-1">Everything is in sync</p>
                <p className="ev-xsmall-p-dark mb-0">No new or removed documents found for any tagged agent.</p>
              </div>

            ) : (
              <>
                {/* Summary */}
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="ev-small-p-dark">
                    <span className="ev-bold-reg">{changes.length}</span> agent{changes.length > 1 ? 's' : ''} with changes
                    {totalAdded > 0 && (
                      <span className="ms-2 sync-docs-text-added">
                        <i className="bi bi-plus-circle-fill me-1"></i>{totalAdded} new
                      </span>
                    )}
                    {totalRemoved > 0 && (
                      <span className="ms-2 sync-docs-text-removed">
                        <i className="bi bi-dash-circle-fill me-1"></i>{totalRemoved} removed
                      </span>
                    )}
                  </div>
                  <div className="d-flex gap-1">
                    <button className="ev-btn-lightblue-fill-round ev-fs-11 px-2 py-1" onClick={selectAll}>Select All</button>
                    <button className="ev-btn-light-fill ev-fs-11 px-2 py-1" onClick={deselectAll}>Deselect All</button>
                  </div>
                </div>

                {/* Agent cards */}
                {changes.map(change => {
                  const isApproved = approvedAgents.has(change.agent_id);
                  return (
                    <div
                      key={change.agent_id}
                      className={`mb-3 overflow-hidden sync-docs-agent-card ${isApproved ? 'sync-docs-agent-card-selected' : 'sync-docs-agent-card-dimmed'}`}
                    >
                      {/* Agent header */}
                      <div
                        className="d-flex align-items-center gap-2 px-3 py-2 sync-docs-agent-header"
                        onClick={() => toggleAgent(change.agent_id)}
                      >
                        <input
                          type="checkbox"
                          checked={isApproved}
                          onChange={() => toggleAgent(change.agent_id)}
                          className="form-check-input m-0"
                          onClick={e => e.stopPropagation()}
                        />
                        <div className="flex-grow-1 overflow-hidden">
                          <div className="d-flex align-items-center gap-1 flex-wrap">
                            <span className="ev-xsmall-p-dark">{change.section_label}</span>
                            <i className="bi bi-chevron-right ev-xsmall-p-dark sync-docs-chevron"></i>
                            <span className="ev-bold-reg ev-small-p-dark">{change.agent_name}</span>
                          </div>
                          <span className="ev-xsmall-p-dark sync-docs-tag-label">
                            Tag: {change.tag}
                          </span>
                        </div>
                        <div className="d-flex gap-2 text-nowrap">
                          {change.added_docs.length > 0 && (
                            <span className="ev-fs-11 ev-bold-reg sync-docs-text-added">+{change.added_docs.length}</span>
                          )}
                          {change.removed_docs.length > 0 && (
                            <span className="ev-fs-11 ev-bold-reg sync-docs-text-removed">-{change.removed_docs.length}</span>
                          )}
                        </div>
                      </div>

                      {/* Doc list */}
                      <div className="px-3 py-2">
                        {change.added_docs.length > 0 && (
                          <div className="mb-2">
                            <span className="ev-xsmall-p-dark ev-bold-reg sync-docs-text-added">New documents to add:</span>
                            {change.added_docs.map(doc => (
                              <div key={doc.doc_id} className="d-flex align-items-center gap-2 py-1 ms-2">
                                <i className="bi bi-file-earmark-plus sync-docs-icon-added"></i>
                                <span className="ev-xsmall-p-dark">{doc.doc_name}</span>
                                {doc.tag && (
                                  <span className="ev-xxsmall-p-blue rounded-pill px-2 py-0 sync-docs-doc-tag">{doc.tag}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        {change.removed_docs.length > 0 && (
                          <div>
                            <span className="ev-xsmall-p-dark ev-bold-reg sync-docs-text-removed">Documents to remove:</span>
                            {change.removed_docs.map(doc => (
                              <div key={doc.doc_id} className="d-flex align-items-center gap-2 py-1 ms-2">
                                <i className="bi bi-file-earmark-minus sync-docs-icon-removed"></i>
                                <span className="ev-xsmall-p-dark sync-docs-strikethrough">{doc.doc_name}</span>
                                {doc.tag && (
                                  <span className="ev-xxsmall-p-blue rounded-pill px-2 py-0 sync-docs-doc-tag-removed">{doc.tag}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>

          {/* Footer */}
          {hasChanges && !isLoading && (
            <div className="d-flex justify-content-between align-items-center px-3 py-2 sync-docs-modal-footer">
              <span className="ev-xsmall-p-dark">{approvedAgents.size} of {changes.length} selected</span>
              <div className="d-flex gap-2">
                <button className="ev-btn-light-fill ev-bold-reg" onClick={onClose}>Cancel</button>
                <button
                  className="ev-btn-blue-fill-round ev-bold-reg"
                  onClick={handleApply}
                  disabled={approvedAgents.size === 0}
                >
                  <i className="bi bi-check2-all me-1"></i>Apply Changes
                </button>
              </div>
            </div>
          )}

          {!hasChanges && !isLoading && (
            <div className="d-flex justify-content-end px-3 py-2 sync-docs-modal-footer">
              <button className="ev-btn-light-fill ev-bold-reg" onClick={onClose}>Close</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default SyncDocsModal;